import { useState, useEffect, useRef, useMemo } from 'react';
import { VideoThumbnailTicks } from './VideoThumbnailTicks';
import { VideoUtilsWorkerClient } from "../Utils/videoUtils/videoUtils.client";
import { formatTime } from '../Utils/ConversionUtils'
import { VideoReadyResult, ThumbnailFrameResult } from "../Utils/videoUtils/videoUtils";
import './css/VideoThumbnailBar.css';

interface onLoadProps {
    width: number;
    frameCount: number;
}

interface VideoThumbnailBarProps {
    video: ArrayBuffer;
    videoMetadata: VideoReadyResult;
    filename: string;
    pixelsPerSecond: number;
    onLoad?: (result: onLoadProps) => void;
    onFrameLoad?: (progress: number | null) => void;
}

export const VideoThumbnailBar: React.FC<VideoThumbnailBarProps> = ({ video, videoMetadata, filename, pixelsPerSecond, onLoad, onFrameLoad }) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const barRef = useRef<HTMLDivElement | null>(null);
    const [thumbnailBarWidth, setThumbnailBarWidth] = useState(0);
    const [thumbnailProgressPercentage, setThumbnailProgressPercentage] = useState(0);

    // Cache keyed by: fileName + numThumbs + start + end
    const [thumbnailCache, setThumbnailCache] =
        useState<Record<string, ThumbnailFrameResult[]>>({});

    const [thumbnailBitmaps, setThumbnailBitmaps] = useState<Array<{
        left: number;
        bitmap: ImageBitmap;
        width: number;
        height: number;
    }>>([]);

    const [ticks, setTicks] = useState<Array<{ t: number; left: number; label: string }>>([]);
    const [minorTicks, setMinorTicks] = useState<Array<{ t: number; left: number }>>([]);

    // Nice human-friendly tick intervals (seconds)
    const NICE_INTERVALS = [
        0.1, 0.2, 0.5,
        1, 2, 5, 10, 15, 30,
        60, 120, 300, 600, 900, 1800, 3600
    ];

    const extractor = useMemo(() => new VideoUtilsWorkerClient(), []);

    useEffect(() => {
        if (!videoMetadata || !pixelsPerSecond) return;

        const durationSecs = videoMetadata.durationSecs;
        const fps = videoMetadata.frameRate;

        // Derived widths
        const W_bar = durationSecs * pixelsPerSecond;

        setThumbnailBarWidth(W_bar);

        onLoad?.({ width: W_bar, frameCount: (durationSecs * fps) });

        // Thumbnail geometry
        const barHeight = barRef.current?.clientHeight ?? 0;
        const thumbWidth = barHeight * videoMetadata.aspectRatio;
        const thumbHeight = barHeight;

        const numThumbs = Math.floor(W_bar / thumbWidth) + 1;

        const startTime = 0;
        const endTime = durationSecs;

        // NEW: video-aware cache key
        const cacheKey = `${filename}-${numThumbs}-${startTime}-${endTime}`;

        const cached = thumbnailCache[cacheKey];

        if (cached && cached.length === numThumbs) {
            // Use cached thumbnails
            const mapped = cached.map(result => ({
                left: result.timestamp * pixelsPerSecond,
                bitmap: result.bitmap,
                width: thumbWidth,
                height: thumbHeight
            }));
            setThumbnailBitmaps(mapped);
        } else {
            // Extract thumbnails for this zoom level
            setThumbnailBitmaps([]);

            extractor.start(
                video,
                numThumbs,
                startTime,
                endTime,
                (result: ThumbnailFrameResult) => {
                    if (!result.isLast) {
                        setThumbnailProgressPercentage((result.index / result.total) * 100);
                    }
                    else {
                        setThumbnailProgressPercentage(0);
                    }

                    // Update cache
                    setThumbnailCache(prev => {
                        const existing = prev[cacheKey] ?? [];
                        return {
                            ...prev,
                            [cacheKey]: [...existing, result]
                        };
                    });

                    // Update rendered thumbnails
                    const left = result.timestamp * pixelsPerSecond;

                    setThumbnailBitmaps(prev => [
                        ...prev,
                        {
                            left: left,
                            bitmap: result.bitmap,
                            width: thumbWidth,
                            height: thumbHeight
                        }
                    ]);
                }, (value: number) => {
                    onFrameLoad?.(value);
                }, () => {
                }, () => {
                    onFrameLoad?.(null);
                }
            );
        }

        // --- Tick generation ---
        const MIN_TICK_PIXELS = 80;
        const majorInterval =
            NICE_INTERVALS.find(v => v * pixelsPerSecond >= MIN_TICK_PIXELS)
            ?? NICE_INTERVALS[NICE_INTERVALS.length - 1];

        const minorDivisions = 5;
        const minorInterval = majorInterval / minorDivisions;

        const newTicks: Array<{ t: number; left: number; label: string }> = [];
        const newMinorTicks: Array<{ t: number; left: number }> = [];

        // Major ticks
        for (let t = 0; t <= durationSecs; t += majorInterval) {
            newTicks.push({
                t,
                left: t * pixelsPerSecond,
                label: formatTime(t)
            });

            // Minor ticks between major ticks
            for (let m = 1; m < minorDivisions; m++) {
                const mt = t + m * minorInterval;
                if (mt <= durationSecs) {
                    newMinorTicks.push({
                        t: mt,
                        left: mt * pixelsPerSecond
                    });
                }
            }
        }

        setTicks(newTicks);
        setMinorTicks(newMinorTicks);
    }, [pixelsPerSecond, videoMetadata, filename]);

    return (
        <div className="video-thumbnail-bar-container" ref={containerRef}>

            <div className={`video-thumbnail-progress ${thumbnailProgressPercentage ? "enabled" : "disabled"}`}>
                <span style={{ width: `${thumbnailProgressPercentage}%` }} />
            </div>

            <div
                className="video-thumbnail-bar"
                ref={barRef}
                style={({
                    "--thumbnailBarWidth": `${thumbnailBarWidth}px`
                } as React.CSSProperties)}>
                {thumbnailBitmaps.map((thumb, i) => (
                    <div className="thumbnail-bitmap-canvas-container">
                        <canvas
                            key={i}
                            width={thumb.width}
                            height={thumb.height}
                            ref={canvas => {
                                if (canvas) {
                                    const ctx = canvas.getContext("2d");
                                    if (ctx) {
                                        ctx.drawImage(
                                            thumb.bitmap,
                                            0, 0,
                                            thumb.bitmap.width,
                                            thumb.bitmap.height,
                                            0, 0,
                                            thumb.width,
                                            thumb.height
                                        );
                                    }
                                }
                            }}
                        />
                    </div>
                ))}
            </div>
            <VideoThumbnailTicks fullDurationPixelWidth={thumbnailBarWidth}
                timelineOffset={0}
                ticks={ticks}
                minorTicks={minorTicks}
            />
        </div>
    );
};
